/**
 * ULID generator — Universally Unique Lexicographically Sortable Identifier.
 * Uses Crockford Base32 encoding. No external dependencies.
 * Spec: https://github.com/ulid/spec
 */

// Crockford Base32 alphabet (excludes I, L, O, U to avoid confusion)
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * Encode a 48-bit timestamp (milliseconds since epoch) as 10 Base32 chars.
 * Max timestamp: 2^48 - 1 = year ~10889.
 */
function encodeTime(ms: number): string {
  let result = ''
  let t = ms
  for (let i = 9; i >= 0; i--) {
    result = ENCODING[t % 32] + result
    t = Math.floor(t / 32)
  }
  return result
}

/**
 * Encode 10 random bytes (80 bits) as 16 Base32 chars.
 * Processes 5-bit groups from the byte stream.
 */
function encodeRandom(bytes: Uint8Array): string {
  // Treat 10 bytes as a 80-bit integer, extract 16 groups of 5 bits
  // We process from least-significant end for simplicity
  let result = ''

  // Build a BigInt from the bytes for clean 5-bit extraction
  let val = BigInt(0)
  for (let i = 0; i < bytes.length; i++) {
    val = (val << BigInt(8)) | BigInt(bytes[i])
  }

  for (let i = 0; i < 16; i++) {
    result = ENCODING[Number(val & BigInt(31))] + result
    val >>= BigInt(5)
  }

  return result
}

export function ulid(): string {
  const now = Date.now()
  const randomBytes = crypto.getRandomValues(new Uint8Array(10))
  return encodeTime(now) + encodeRandom(randomBytes)
}

export function todoId(): string {
  return 'td_' + ulid()
}

export function sectionId(): string {
  return 'sec_' + ulid()
}

export function noteId(): string {
  return 'note_' + ulid()
}

export function recurrenceId(): string {
  return 'rec_' + ulid()
}

/** Recognise the synthetic virtual-occurrence id the server emits for unmaterialised recurring instances. */
export function isVirtualTodoId(id: string): boolean {
  return id.startsWith('vri_')
}

export function idempotencyKey(): string {
  return ulid()
}

/** Validate that a string looks like a ULID (26 Crockford Base32 chars) */
export function isUlid(s: string): boolean {
  return /^[0-9A-HJKMNP-TV-Z]{26}$/.test(s)
}
